import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

// packages
import { useTranslation } from "react-i18next";
import classNames from "classnames";

// types
import {
  ESenderRole,
  IContractActionsResponseData,
} from "../../../../setup/api/services/contract/models";

// context provider
import { ContractContext } from "../../../../providers/contract";

// helpers
import { formatDateToArabic } from "../../../../setup/util/helpers";

// styles
import styles from "./StatusTimeLine.module.scss";

// apis
import { getContractActions } from "../../../../setup/api/services/contract";

// types
import { EContractActions } from "../../../../setup/api/services/contractActions/enum";
import { ECONTRACT_STATUS } from "../../../../enums";

enum CONTRACTSTATE {
  ACTIVE = "ACTIVE",
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
}

const StatusTimeLine = () => {
  const { t } = useTranslation();
  const { contract } = useContext(ContractContext);
  const [stateActions, setStateActions] = useState<{
    [key: IContractActionsResponseData["action"]]: IContractActionsResponseData;
  }>({});

  const status = useMemo(() => {
    return contract?.status;
  }, [contract?.status]);

  const getStatusActions = useCallback(async (id: string) => {
    try {
      const response = await getContractActions(id);

      const actionsObject: { [key: string]: IContractActionsResponseData } = {};
      response?.data?.forEach((action) => {
        if (
          action?.action === EContractActions.RECEIVER_ACCEPT_CONTRACT &&
          action?.actorRole === ESenderRole.PROVIDER
        ) {
          return;
        } else {
          actionsObject[action.action] = action;
        }
      });
      setStateActions(actionsObject);
    } catch (error) {
      console.error;
    }
  }, []);

  useEffect(() => {
    if (contract?.id) getStatusActions(contract?.id);
  }, [contract?.id, getStatusActions]);

  const verifyAmount = useCallback(
    (stateActions: {
      [
        key: IContractActionsResponseData["action"]
      ]: IContractActionsResponseData;
    }) => {
      if (
        stateActions[
          `${EContractActions.CUSTOMER_REQUEST_PAYMENT_VERIFICATION}`
        ]
      ) {
        if (stateActions[`${EContractActions.ADMIN_VERIFY_PAYMENT}`])
          return {
            title: `${t("lbl_amount_deposited")}`,
            contractState: CONTRACTSTATE.COMPLETED,
          };
        return {
          title: `${t("lbl_verify_deposit_amount")}`,
          contractState: CONTRACTSTATE.PENDING,
        };
      }
      if (stateActions[`${EContractActions.CUSTOMER_FUND}`])
        return {
          title: `${t("lbl_amount_deposited")}`,
          contractState: CONTRACTSTATE.COMPLETED,
        };
      return {
        title: `${t("lbl_deposit_amount")}`,
        contractState: CONTRACTSTATE.ACTIVE,
      };
    },
    [t]
  );

  const verifyAmountDate = useCallback(
    (stateActions: {
      [
        key: IContractActionsResponseData["action"]
      ]: IContractActionsResponseData;
    }) => {
      return (
        stateActions[`${EContractActions.ADMIN_VERIFY_PAYMENT}`]?.createdAt ||
        stateActions[`${EContractActions.CUSTOMER_FUND}`]?.createdAt ||
        stateActions[
          `${EContractActions.CUSTOMER_REQUEST_PAYMENT_VERIFICATION}`
        ]?.createdAt
      );
    },
    []
  );

  const resolvedState = useCallback(() => {
    if (
      stateActions[`${EContractActions.BROKER_ACCEPT_ITEM}`] ||
      stateActions[`${EContractActions.ADMIN_RELEASE_FUND_TO_SELLER}`]
    )
      return true;
    return false;
  }, [stateActions]);

  const refundState = useCallback(() => {
    if (
      stateActions[`${EContractActions.BROKER_REFUND_CUSTOMER}`] ||
      stateActions[
        `${EContractActions.ADMIN_RESOLVE_ESCALATION_RELEASE_PROVIDER}`
      ] ||
      stateActions[`${EContractActions.ADMIN_REFUND_CUSTOMER}`]
    )
      return true;
    return false;
  }, [stateActions]);

  const statuses = useMemo((): {
    title?: string;
    actionDate?: IContractActionsResponseData["createdAt"];
    contractState: CONTRACTSTATE;
  }[] => {
    return [
      {
        title: `${t("CREATE_TRANSACTION")}`,
        actionDate:
          stateActions[`${EContractActions.BROKER_CREATE_CONTRACT}`]?.createdAt,
        contractState: stateActions[
          `${EContractActions.BROKER_CREATE_CONTRACT}`
        ]
          ? CONTRACTSTATE.COMPLETED
          : CONTRACTSTATE?.ACTIVE,
      },
      {
        contractState: CONTRACTSTATE?.ACTIVE,
      },
      {
        title:
          stateActions[`${EContractActions.RECEIVER_ACCEPT_CONTRACT}`]
            ?.actorRole === ESenderRole.CUSTOMER
            ? `${t("lbl_depositor_joined")}`
            : `${t("lbl_depositor_joining")}`,
        actionDate:
          stateActions[`${EContractActions.RECEIVER_ACCEPT_CONTRACT}`]
            ?.actorRole === ESenderRole.CUSTOMER
            ? stateActions[`${EContractActions.RECEIVER_ACCEPT_CONTRACT}`]
                ?.createdAt
            : undefined,
        contractState:
          stateActions[`${EContractActions.RECEIVER_ACCEPT_CONTRACT}`]
            ?.actorRole === ESenderRole.CUSTOMER
            ? CONTRACTSTATE.COMPLETED
            : CONTRACTSTATE?.ACTIVE,
      },
      {
        contractState: CONTRACTSTATE?.ACTIVE,
      },
      {
        title: verifyAmount(stateActions)?.title,
        actionDate: verifyAmountDate(stateActions),
        contractState: verifyAmount(stateActions)?.contractState,
      },
      {
        contractState: CONTRACTSTATE?.ACTIVE,
      },
      {
        title: refundState()
          ? `${t("lbl_broker_refund_customer")}`
          : resolvedState()
          ? `${t("lbl_broker_release_seller")}`
          : status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY
          ? `${t("lbl_verify_implementing_payment")}`
          : status === ECONTRACT_STATUS.ITEM_ACCEPTED
          ? `${t("lbl_verify_implementing_payment")}`
          : `${t("lbl_implementing_payment")}`,
        actionDate:
          stateActions[`${EContractActions.BROKER_REFUND_CUSTOMER}`]
            ?.createdAt ||
          stateActions[`${EContractActions.BROKER_ACCEPT_ITEM}`]?.createdAt,
        contractState:
          refundState() ||
          stateActions[`${EContractActions.BROKER_ACCEPT_ITEM}`]
            ? CONTRACTSTATE.COMPLETED
            : status === ECONTRACT_STATUS.WAITING_FOR_DELIVERY
            ? CONTRACTSTATE?.PENDING
            : status === ECONTRACT_STATUS.ITEM_ACCEPTED
            ? CONTRACTSTATE.COMPLETED
            : CONTRACTSTATE.ACTIVE,
      },
      {
        contractState: CONTRACTSTATE?.ACTIVE,
      },
      {
        title:
          status === ECONTRACT_STATUS.COMPLETED ||
          status === ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER
            ? stateActions[
                `${EContractActions.ADMIN_COMPLETE_RELEASE_FUND_TO_PROVIDER}`
              ]
              ? `${t("lbl_admin_broker_seller_release")}`
              : `${t("lbl_admin_broker_buyer_refund")}`
            : `${t("lbl_money_transfer")}`,
        actionDate:
          stateActions[
            `${EContractActions.ADMIN_COMPLETE_RELEASE_FUND_TO_PROVIDER}`
          ]?.createdAt ||
          stateActions[`${EContractActions.ADMIN_CLOSE_REFUND_CUSTOMER}`]?.createdAt,
        contractState:
          status === ECONTRACT_STATUS.COMPLETED ||
          status === ECONTRACT_STATUS.CLOSED_REFUND_CUSTOMER
            ? CONTRACTSTATE.COMPLETED
            : CONTRACTSTATE?.ACTIVE,
      },
    ];
  }, [
    refundState,
    resolvedState,
    stateActions,
    status,
    t,
    verifyAmount,
    verifyAmountDate,
  ]);

  const activeImageSource = useCallback((contractState: CONTRACTSTATE) => {
    switch (contractState) {
      case CONTRACTSTATE.ACTIVE:
        return "/assets/images/svg/activeDisabled.svg";
      case CONTRACTSTATE.COMPLETED:
        return "/assets/images/svg/active.svg";
      case CONTRACTSTATE.PENDING:
        return "/assets/images/svg/activePending.svg";
      default:
        return "/assets/images/svg/activeDisabled.svg";
    }
  }, []);

  return (
    <div className={styles["container"]}>
      <h4>{t("lbl_what_should_be_done")}</h4>
      <div className={styles["status_container"]}>
        {statuses?.map((status) => (
          <>
            <div className={styles["status"]}>
              <div className={styles["image"]}>
                <img
                  alt={status?.title}
                  src={
                    status?.title
                      ? activeImageSource(status?.contractState)
                      : "/assets/images/svg/statusDivider.svg"
                  }
                />
              </div>
              <div className={styles["row_stretched"]}>
                <p className={styles["subtitle4"]}>{status?.title}</p>
                <p className={classNames(styles["date"], styles["g400"])}>
                  {formatDateToArabic(status?.actionDate?.toString())}
                </p>
              </div>
            </div>
          </>
        ))}
      </div>
    </div>
  );
};

export default StatusTimeLine;
